@use '@/styles/tokens' as *;

// Figma 1790:954280: a 345px card, 10px padding, 12px radius, 18px between regions.
.menu {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 345px;
  max-width: calc(100vw - #{$space-8});
  padding: var(--space-2);
  border: 1px solid $color-light-brown-200;
  border-radius: var(--radius-xl);
  background: var(--surface-card);
  box-shadow: var(--shadow-overlay);
}

// ── Identity header ─────────────────────────────────────────────────────────
.identity {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);
  border-radius: var(--radius-lg);
  background: $color-light-brown-100;
}

.identityHead {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.name {
  margin: 0;
  font-family: $font-family-base;
  font-size: $font-size-18;
  font-weight: $font-weight-regular;
  line-height: 1;
  color: var(--text-primary);
  // Long names must not push the card wider than the menu.
  @include truncate;
}

// Edit Profile (US-117543, grilling Q5): top-right of the header card.
.edit {
  @include reset-button;
  @include focus-ring(var(--border-interactive));

  display: inline-flex;
  flex: none;
  margin-inline-start: auto;
  border-radius: var(--radius-pill);
  color: var(--text-secondary);
}

.contacts {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.contact {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  font-size: $font-size-14;
  line-height: 1;
  color: var(--text-secondary);

  > span {
    @include truncate;
  }

  svg {
    flex: none;
    color: var(--text-secondary);
  }
}

// ── Counter strip ───────────────────────────────────────────────────────────
// Three buttons with hairline dividers between, not around. Each opens Devices preset to its status.
.stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-inline: var(--space-2);
}

.stat {
  @include reset-button;
  @include focus-ring(var(--border-interactive));

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(var(--space-1) / 2);
  border-radius: var(--radius-lg);
}

.statValue {
  font-family: $font-family-heading;
  font-size: $font-size-24;
  font-weight: $font-weight-medium;
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--text-primary);
}

.srOnly {
  @include visually-hidden;
}

.statSkeleton {
  width: var(--space-8);
  height: var(--space-6);
  border-radius: var(--radius-sm);
  background-color: $color-light-brown-100;
  @include shimmer;
}

.statLabel {
  font-size: $font-size-13;
  color: var(--text-muted);
}

.divider {
  flex: none;
  width: 1px;
  height: 25px; // Figma 1790:954298; no token
  background: $color-neutral-300;
}

// ── Shortcut tiles ──────────────────────────────────────────────────────────
.cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

.card {
  @include reset-button;
  @include focus-ring(var(--border-interactive));

  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-6) var(--space-3) var(--space-3);
  border: 0.5px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  background: $color-brand-700;
  color: var(--text-inverse);
  text-align: left;

  &:hover {
    background: $color-brand-800;
  }
}

.cardIcon {
  color: $color-accent1-300;
}

// Figma 1790:954318 keeps "Manage Subscriptions" on one line, running into the tile's right
// padding rather than wrapping.
.cardLabel {
  font-size: $font-size-13;
  line-height: 1;
  color: var(--text-inverse);
  white-space: nowrap;
}

// Top-right of the tile, above the icon row rather than beside the label. No clamp on the count.
.cardBadge {
  position: absolute;
  top: var(--space-2);
  right: var(--space-3);
  display: flex;
  align-items: center;
  height: 16px; // Figma 1790:954311; no token
  padding-inline: var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--overlay-40);
  font-size: $font-size-9;
  font-weight: $font-weight-medium;
  line-height: 1;
  color: var(--text-inverse);
  white-space: nowrap;
}

// ── Menu list ───────────────────────────────────────────────────────────────
.list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.item {
  @include reset-button;
  @include focus-ring(var(--border-interactive));

  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-3) var(--space-2);
  border-radius: var(--radius-lg);
  font-size: $font-size-15;
  line-height: 1;
  color: var(--text-tertiary);
  text-align: left;

  &:hover {
    background: var(--surface-subtle);
  }
}

.itemIcon {
  display: flex;
  flex: none;
  color: $color-neutral-600;
}

.danger {
  color: $color-error-900;

  .itemIcon {
    color: $color-error-900;
  }
}
